/**
 * V2StepPasteProcessor - Pure data transformation for V2 StepNode paste operations
 *
 * Takes clipboard V2 steps and produces ready-to-persist steps with:
 * - New unique IDs (crypto.randomUUID)
 * - Deduplicated names (avoids collisions with existing steps + within the
 *   paste batch itself — "Name" → "Name (Copy)" → "Name (Copy 2)" → ...)
 * - Position offset (default {x:150, y:150}, configurable)
 * - Edge rewriting: intra-clipboard edges are remapped to new IDs;
 *   external edges (pointing to steps NOT in the clipboard) are dropped
 * - RouteGuard condition-key rewriting: data-path references to
 *   clipboard step IDs (e.g. `oldId.input['email'].value`) are updated
 *   to the new IDs via `dataPathBuilder.parsePath()`
 *
 * Unlike V1's `ScreenPasteProcessor`, this implementation does NOT apply
 * a locale A+B transform — V2 pastes content as-is. Translations stored
 * under `content.*.translations` are preserved verbatim.
 *
 * The edge walker is deliberately **generic** rather than maintaining a
 * hand-coded location table. It detects the V2 `Trigger` shape
 * (`{ kind: string, edges: Edge[] }`) anywhere inside `step.content` and
 * rewrites each `edges` array it finds. This automatically covers every
 * edge location enumerated by `V2TransitionManager.extractTransitions()`
 * (actionBar, header, options.items, carousel.timer, stepper.timer,
 * countdown, systemPrompt, embed.callback, paywall.*, flexibleLayout
 * sections, stickyContainer sections) plus any future schema additions,
 * and handles nested Triggers via `onCancelAction` (downsale).
 */

import type { StepNode, Route as V2Edge } from '@segmently/schema-models/v2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { dataPathBuilder } from '@modules/screen-utils/services/DataPathBuilder';

/**
 * Input for the paste processor
 */
export interface V2PasteProcessorInput {
  /** Deep-cloned V2 steps from clipboard */
  clipboardSteps: StepNode[];
  /** Original step IDs (same order as clipboardSteps) */
  originalStepIds: string[];
  /** Names of existing steps in the target onboarding (for dedup) */
  existingStepNames: string[];
  /** Position offset to apply to all pasted steps (default {150,150}) */
  positionOffset?: { x: number; y: number };
}

/**
 * Output of the paste processor
 */
export interface V2PasteProcessorOutput {
  /** Processed V2 steps ready for Firebase persistence */
  steps: FlowStepNode[];
  /** Mapping from old step IDs to new step IDs */
  idMapping: Map<string, string>;
}

/**
 * Internal type for a detected V2 Trigger inside step content.
 * Matches the schema shape `{ kind: string, edges: V2Edge[], onCancelAction?: Trigger }`.
 */
interface TriggerLike {
  kind: string;
  edges: V2Edge[];
  onCancelAction?: TriggerLike;
}

/**
 * Service for processing clipboard V2 steps into paste-ready entities.
 */
export class V2StepPasteProcessor {
  /**
   * Process clipboard V2 steps for paste.
   *
   * Pipeline (in order):
   * 1. Generate new UUIDs and old→new ID mapping
   * 2. Build clipboard ID set (for internal-vs-external edge classification)
   * 3. Deep clone each step (isolates the paste output from clipboard data)
   * 4. Deduplicate names (tracks names assigned in THIS batch too)
   * 5. Apply position offset
   * 6. Rewrite edges via generic Trigger walker
   * 7. Rewrite RouteGuard condition keys for intra-clipboard references
   * 8. Assign new entityId / id (must be last — after all transformations)
   */
  process(input: V2PasteProcessorInput): V2PasteProcessorOutput {
    const {
      clipboardSteps,
      originalStepIds,
      existingStepNames,
      positionOffset = { x: 150, y: 150 },
    } = input;

    // Step 1-2: Generate new IDs and build mapping + clipboard set
    const idMapping = this.generateIdMapping(originalStepIds);
    const clipboardIdSet = new Set(originalStepIds);

    // Track all names (existing + already assigned in this batch) for dedup
    const allUsedNames = new Set(existingStepNames);

    const processedSteps: FlowStepNode[] = clipboardSteps.map((step, index) => {
      const oldId = originalStepIds[index];
      const newId = idMapping.get(oldId)!;

      // Step 3: Deep clone (avoid mutating clipboard data)
      const cloned = JSON.parse(JSON.stringify(step)) as StepNode;

      // Step 4: Dedup name
      const originalName = cloned.name || 'Screen';
      const dedupedName = this.deduplicateName(originalName, allUsedNames);
      cloned.name = dedupedName;
      allUsedNames.add(dedupedName);

      // Step 5: Apply position offset
      const origPosition = (cloned as any).position || { x: 0, y: 0 };
      (cloned as any).position = {
        x: (origPosition.x || 0) + positionOffset.x,
        y: (origPosition.y || 0) + positionOffset.y,
      };

      // Step 6: Rewrite edges (intra-clipboard preserved + remapped, external dropped)
      this.rewriteAllEdges(cloned, idMapping, clipboardIdSet);

      // Step 7: Rewrite RouteGuard condition keys
      this.rewriteAllConditionKeys(cloned, idMapping);

      // Step 8: Assign new IDs (must be after all transformations)
      return {
        ...cloned,
        id: newId,
        entityId: newId,
      } as FlowStepNode;
    });

    return { steps: processedSteps, idMapping };
  }

  /**
   * Generate old→new ID mapping using crypto.randomUUID().
   */
  private generateIdMapping(originalIds: string[]): Map<string, string> {
    const mapping = new Map<string, string>();
    for (const oldId of originalIds) {
      mapping.set(oldId, crypto.randomUUID());
    }
    return mapping;
  }

  /**
   * Deduplicate a step name against existing + batch-assigned names.
   * Pattern: "Name" → "Name (Copy)" → "Name (Copy 2)" → "Name (Copy 3)" → ...
   *
   * If the input name already has a `(Copy)` / `(Copy N)` suffix, it is
   * stripped first so repeated pastes don't accumulate nested suffixes.
   */
  private deduplicateName(name: string, usedNames: Set<string>): string {
    if (!usedNames.has(name)) {
      return name;
    }

    // Strip existing " (Copy)" or " (Copy N)" suffix to get base name
    const baseName = name.replace(/ \(Copy( \d+)?\)$/, '');
    let counter = 1;

    while (true) {
      const candidate = counter === 1
        ? `${baseName} (Copy)`
        : `${baseName} (Copy ${counter})`;

      if (!usedNames.has(candidate)) {
        return candidate;
      }
      counter++;
    }
  }

  /**
   * Rewrite all edges on a cloned step's content.
   *
   * Walks the step's `content` tree generically, detecting any object
   * whose shape matches V2 `Trigger` (`{ kind, edges }`). For each Trigger
   * found, rewrites its `edges` array:
   * - keeps edges whose target is in the clipboard → remaps target ID
   * - drops edges whose target is NOT in the clipboard
   *
   * Recurses into nested `onCancelAction` triggers (downsale flows).
   */
  private rewriteAllEdges(
    step: StepNode,
    idMapping: Map<string, string>,
    clipboardIds: Set<string>
  ): void {
    const content = (step as any).content;
    if (!content) return;

    this.walkTriggers(content, (trigger) => {
      trigger.edges = trigger.edges
        .filter((edge) => edge && edge.nextScreenId && clipboardIds.has(edge.nextScreenId))
        .map((edge) => ({
          ...edge,
          nextScreenId: idMapping.get(edge.nextScreenId!)!,
        }));
    });
  }

  /**
   * Rewrite RouteGuard condition keys (`edge.conditions[].key`) that embed
   * clipboard screen IDs.
   *
   * Uses `dataPathBuilder.parsePath()` to extract the leading screenId
   * from data paths like `{screenId}.list.checked` or
   * `{screenId}.input['field'].value`. If the parsed screenId is in the
   * clipboard ID set, the key is rewritten with the new ID.
   *
   * Walks the same Trigger tree as edge rewriting.
   */
  private rewriteAllConditionKeys(
    step: StepNode,
    idMapping: Map<string, string>
  ): void {
    const content = (step as any).content;
    if (!content) return;

    this.walkTriggers(content, (trigger) => {
      for (const edge of trigger.edges) {
        if (!edge || !Array.isArray((edge as any).conditions)) continue;
        for (const cond of (edge as any).conditions) {
          if (!cond || typeof cond.key !== 'string') continue;
          const parsed = dataPathBuilder.parsePath(cond.key);
          if (parsed?.screenId && idMapping.has(parsed.screenId)) {
            const newId = idMapping.get(parsed.screenId)!;
            cond.key = cond.key.replace(parsed.screenId, newId);
          }
        }
      }
    });
  }

  /**
   * Generic recursive walker that visits every V2 Trigger inside a
   * content subtree and invokes `visit(trigger)` on it.
   *
   * A node is considered a Trigger when it is a non-array object with
   * both a string `kind` field and an `edges` array. This matches every
   * V2 Trigger location (actionBar, header, options.items, timers,
   * countdown, systemPrompt, embed.callback, paywall buttons,
   * flexibleLayout sections, stickyContainer sections) without needing
   * a hand-coded path table, AND picks up any new location added by
   * future schema iterations automatically.
   *
   * Nested `onCancelAction` triggers (downsale) are visited recursively.
   */
  private walkTriggers(node: unknown, visit: (trigger: TriggerLike) => void): void {
    if (!node || typeof node !== 'object') return;

    if (Array.isArray(node)) {
      for (const item of node) {
        this.walkTriggers(item, visit);
      }
      return;
    }

    const obj = node as Record<string, unknown>;

    // Detect Trigger shape: { kind: string, edges: unknown[] }
    if (
      typeof obj.kind === 'string' &&
      Array.isArray(obj.edges)
    ) {
      const trigger = obj as unknown as TriggerLike;
      visit(trigger);

      // Recurse into nested onCancelAction (Trigger-inside-Trigger for downsale)
      if (trigger.onCancelAction) {
        this.walkTriggers(trigger.onCancelAction, visit);
      }

      // Continue walking the rest of the Trigger's fields in case a
      // Trigger contains nested Triggers beyond onCancelAction (defensive).
      for (const [key, value] of Object.entries(obj)) {
        if (key === 'edges' || key === 'kind' || key === 'onCancelAction') continue;
        this.walkTriggers(value, visit);
      }
      return;
    }

    // Not a Trigger — recurse into children
    for (const value of Object.values(obj)) {
      this.walkTriggers(value, visit);
    }
  }
}

/** Singleton instance */
export const v2StepPasteProcessor = new V2StepPasteProcessor();
